import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { BLACK } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import SectionCard from '../../positionTemplate/components/SectionCard';
import CurrencyField from '../components/CurrencyField';
import FormFieldRow from '../components/FormFieldRow';
import { getSectionAnchorId, SECTION_IDS, SHOW_BILL_RATE_NOTE } from '../constants';

const PAY_RATE_FIELDS = [
  {
    key: 'reg',
    id: 'position-template-pay-rate-reg',
    labelKey: 'positionTemplate.create.payRates.reg.label',
  },
  {
    key: 'ot',
    id: 'position-template-pay-rate-ot',
    labelKey: 'positionTemplate.create.payRates.ot.label',
  },
  {
    key: 'dt',
    id: 'position-template-pay-rate-dt',
    labelKey: 'positionTemplate.create.payRates.dt.label',
  },
];

const BILL_RATE_FIELDS = [
  {
    key: 'reg',
    id: 'position-template-bill-rate-reg',
    labelKey: 'positionTemplate.create.billRates.reg.label',
  },
  {
    key: 'ot',
    id: 'position-template-bill-rate-ot',
    labelKey: 'positionTemplate.create.billRates.ot.label',
  },
  {
    key: 'dt',
    id: 'position-template-bill-rate-dt',
    labelKey: 'positionTemplate.create.billRates.dt.label',
  },
];

const rateFieldsShape = PropTypes.arrayOf(
  PropTypes.shape({
    key: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    labelKey: PropTypes.string.isRequired,
  })
);

const ratesShape = PropTypes.shape({
  reg: PropTypes.string,
  ot: PropTypes.string,
  dt: PropTypes.string,
});

const RateRow = ({ fields, rates, onChange }) => (
  <Box
    sx={{
      display: 'grid',
      gap: '16px',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
    }}
  >
    {fields.map(({ key, id, labelKey }) => (
      <FormFieldRow key={key} labelKey={labelKey} htmlFor={id}>
        <CurrencyField
          id={id}
          value={rates?.[key] ?? ''}
          onChange={(nextValue) => onChange({ ...rates, [key]: nextValue })}
          testId={id}
        />
      </FormFieldRow>
    ))}
  </Box>
);

RateRow.propTypes = {
  fields: rateFieldsShape.isRequired,
  rates: ratesShape,
  onChange: PropTypes.func.isRequired,
};

const BillRateNote = () => {
  const noteTitle = LanguageConverterV2('positionTemplate.create.payRates.billRateNote.title');
  const noteBody = LanguageConverterV2('positionTemplate.create.payRates.billRateNote.body');

  return (
    <Box
      data-testid="bill-rate-note"
      sx={{
        alignItems: 'flex-start',
        backgroundColor: BLACK[10],
        border: `1px solid ${BLACK[30]}`,
        borderRadius: '6px',
        color: BLACK[70],
        display: 'flex',
        gap: '8px',
        padding: '10px 12px',
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: '16px', marginTop: '1px' }} />
      <Typography sx={{ color: 'inherit', fontFamily: PRIMARY_FONT[400], fontSize: '13px' }}>
        <Box component="strong" sx={{ color: BLACK[100], fontFamily: PRIMARY_FONT[700] }}>
          {noteTitle}
        </Box>{' '}
        {noteBody}
      </Typography>
    </Box>
  );
};

const PayRatesSection = ({ values, setField }) => {
  const title = LanguageConverterV2('positionTemplate.create.payRates.title');

  return (
    <Box
      id={getSectionAnchorId(SECTION_IDS.PAY_RATES)}
      data-testid="pay-rates-section"
      sx={{ scrollMarginTop: '16px' }}
    >
      <SectionCard
        header={
          <Typography sx={{ color: BLACK[100], fontFamily: PRIMARY_FONT[700], fontSize: '14px' }}>
            {title}
          </Typography>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
          <RateRow
            fields={PAY_RATE_FIELDS}
            rates={values.payRates}
            onChange={(next) => setField('payRates', next)}
          />
          <RateRow
            fields={BILL_RATE_FIELDS}
            rates={values.billRates}
            onChange={(next) => setField('billRates', next)}
          />
          {SHOW_BILL_RATE_NOTE && <BillRateNote />}
        </Box>
      </SectionCard>
    </Box>
  );
};

PayRatesSection.propTypes = {
  values: PropTypes.shape({
    payRates: ratesShape,
    billRates: ratesShape,
  }).isRequired,
  setField: PropTypes.func.isRequired,
};

export default PayRatesSection;
